import { useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MoreHorizontal, FileText, Receipt, RotateCcw, Banknote, XCircle, UserX, Loader2, Link2, Pencil, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import SendBookingDialog from "@/components/booking/SendBookingDialog";
import ManualBookingEditDialog from "@/components/booking/ManualBookingEditDialog";
import PaymentsSheet from "@/components/ops/PaymentsSheet";
import { useAuth } from "@/lib/auth";
import { impersonate } from "@/components/AdminImpersonationBanner";

/**
 * Per-booking action menu used by the vendor + admin bookings lists. Five operations:
 *
 *   1. View Details   → /agreements/:id (existing page)
 *   2. Invoice        → GET /api/bookings/:id/invoice.pdf (streamed, opens download)
 *   3. Refund Payment → POST /api/bookings/:id/refund   (refunds rental amount via Stripe)
 *   4. Refund Deposit → POST /api/bookings/:id/deposit/release  (existing endpoint)
 *   5. Cancel Reservation → POST /api/bookings/:id/cancel   (sets status='cancelled')
 *
 * Each destructive op (3/4/5) routes through a single confirm dialog whose copy is built
 * from the op metadata — keeps the UX consistent and lets us add ops later without forking
 * the dialog. After any successful action we invoke `onChanged()` so the parent list
 * re-fetches and the booking row reflects the new status.
 *
 * Gating: the menu items reveal/dim based on booking state. We never hide everything,
 * because admins still need View Details + Invoice on cancelled bookings (for the audit
 * trail). Refund-deposit only appears when there's a held deposit to release.
 */
const ACTIONS = {
  refund_payment: {
    icon: RotateCcw,
    label: "Refund payment",
    title: "Refund rental payment?",
    description: "Refunds the full rental amount (not the deposit) to the renter's card via Stripe. This is permanent.",
    confirm: "Refund payment",
    confirmVariant: "destructive",
    request: (id) => api.post(`/bookings/${id}/refund`).then((r) => r.data),
    successToast: (res) => `Refunded €${res.amount?.toFixed(2)} to the renter.`,
  },
  refund_deposit: {
    icon: Banknote,
    label: "Refund deposit",
    title: "Release the deposit?",
    description: "Refunds the held security deposit in full. Use this when the car returned without damage.",
    confirm: "Release deposit",
    confirmVariant: "default",
    request: (id) => api.post(`/bookings/${id}/deposit/release`).then((r) => r.data),
    successToast: (res) => `Deposit of €${res.amount?.toFixed(2)} released.`,
  },
  cancel: {
    icon: XCircle,
    label: "Cancel reservation",
    title: "Cancel this reservation?",
    description: "Marks the booking as cancelled and frees the car's calendar slot. The renter will need a separate refund — use Refund Payment / Refund Deposit afterwards if needed.",
    confirm: "Cancel reservation",
    confirmVariant: "destructive",
    request: (id) => api.post(`/bookings/${id}/cancel`).then((r) => r.data),
    successToast: () => "Reservation cancelled.",
  },
  // Iter 68 — No-Show: only available after the grace period elapses; the
  // backend returns 422 with `code: "grace_period_not_elapsed"` if the
  // vendor jumps the gun, and the toast surfaces the earliest-markable
  // timestamp so the vendor knows exactly when to retry.
  mark_no_show: {
    icon: UserX,
    label: "Mark No Show",
    title: "Mark booking as No Show?",
    description:
      "The renter never collected the car at the scheduled pickup time. " +
      "Marking this returns the vehicle to availability, adds the vendor's no-show fee " +
      "(if configured) to the rental invoice, AUTO-CAPTURES the fee from the held " +
      "Stripe security deposit when one exists, notifies both parties, and counts toward " +
      "the customer's repeat-no-show flag. This is reversible only by an admin.",
    confirm: "Mark No Show",
    confirmVariant: "destructive",
    request: (id) => api.post(`/bookings/${id}/mark-no-show`, {}).then((r) => r.data),
    // Iter 69 — surface the deposit-capture outcome so vendors know whether
    // they actually got paid or still need to collect offline.
    successToast: (res) => {
      const fee = (res.fee_eur || 0).toFixed(2);
      const cap = res.deposit_capture || {};
      if (cap.action === "captured") {
        return `No Show · €${cap.amount.toFixed(2)} captured from deposit.`;
      }
      if (cap.action === "skipped" && cap.reason === "no_deposit_held") {
        return `Marked No Show — fee €${fee} added to invoice (no deposit on file; please collect offline).`;
      }
      if (cap.action === "skipped" && cap.reason === "no_fee_configured") {
        return `Marked No Show. No fee configured — set one in Settings → No-show policy.`;
      }
      if (cap.action === "failed") {
        return `Marked No Show, but deposit capture failed: ${cap.reason}. Use Withhold Deposit manually.`;
      }
      return `Marked No Show — fee €${fee}.`;
    },
  },
};

export default function BookingActionsMenu({ booking, onChanged }) {
  const [sendOpen, setSendOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const { user: authUser } = useAuth();
  const isRealAdmin = authUser?.role === "admin" && !authUser?.impersonated_by;
  const navigate = useNavigate();
  const [pendingKey, setPendingKey] = useState(null); // which op is awaiting confirmation
  const [busy, setBusy] = useState(false);

  const isCancelled = booking.status === "cancelled";
  const isNoShow = booking.status === "no_show";
  const isConfirmed = booking.status === "confirmed";
  const isRefunded = booking.payment_status === "refunded";
  const hasHeldDeposit = (booking.deposit > 0) && (!booking.deposit_status || booking.deposit_status === "held");

  // Stream the invoice PDF using a credentialed fetch so the httpOnly auth cookie travels.
  // Iter 87 — lang "en" | "sq" picks the invoice language.
  const downloadInvoice = async (lang = "en") => {
    try {
      const res = await fetch(`${API}/bookings/${booking.id}/invoice.pdf?lang=${lang}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${booking.id.slice(0, 8).toUpperCase()}${lang === "sq" ? "-SQ" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error("Could not generate invoice. Please try again.");
    }
  };

  const runPending = async () => {
    const op = ACTIONS[pendingKey];
    if (!op) return;
    setBusy(true);
    try {
      const res = await op.request(booking.id);
      toast.success(op.successToast(res));
      setPendingKey(null);
      onChanged?.();
    } catch (e) {
      // Iter 68 — surface the structured `grace_period_not_elapsed` error
      // from /mark-no-show with the human-readable earliest-markable time,
      // otherwise fall back to the generic API error formatter.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "grace_period_not_elapsed") {
        // Format the ISO timestamp into the renter's locale + timezone so
        // vendors don't have to mentally parse '2035-11-01T12:00:00+00:00'.
        let when = detail.earliest_markable_at;
        try {
          when = new Date(detail.earliest_markable_at).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
          });
        } catch { /* keep raw ISO if Date parsing fails */ }
        toast.error(`Grace period not elapsed yet — you can mark No Show after ${when} (${detail.grace_minutes} min after pickup).`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full h-8 px-2.5"
            data-testid={`booking-actions-${booking.id}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid={`booking-actions-menu-${booking.id}`}>
          <DropdownMenuItem onClick={() => navigate(`/agreements/${booking.id}`)} data-testid={`booking-action-details-${booking.id}`}>
            <FileText className="h-4 w-4 mr-2" /> View details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setEditOpen(true)} data-testid={`booking-action-edit-${booking.id}`}>
            <Pencil className="h-4 w-4 mr-2" /> Edit booking
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setSendOpen(true)} data-testid={`booking-action-send-${booking.id}`}>
            <Send className="h-4 w-4 mr-2" /> Email / WhatsApp customer
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setPaymentsOpen(true)} data-testid={`booking-action-payments-${booking.id}`}>
            <Banknote className="h-4 w-4 mr-2" /> Payments
          </DropdownMenuItem>
          {isRealAdmin && (
            <>
              <DropdownMenuItem onClick={() => impersonate(booking.vendor_id, "vendor").catch(() => toast.error("Could not open vendor dashboard"))} data-testid={`booking-action-open-vendor-${booking.id}`}>
                <ExternalLink className="h-4 w-4 mr-2" /> Open vendor dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => impersonate(booking.customer_id, "customer").catch(() => toast.error("Could not open client dashboard"))} data-testid={`booking-action-open-client-${booking.id}`}>
                <ExternalLink className="h-4 w-4 mr-2" /> Open client dashboard
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem onClick={() => downloadInvoice("en")} data-testid={`booking-action-invoice-${booking.id}`}>
            <Receipt className="h-4 w-4 mr-2" /> Download invoice (EN)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => downloadInvoice("sq")} data-testid={`booking-action-invoice-sq-${booking.id}`}>
            <Receipt className="h-4 w-4 mr-2" /> Fatura në shqip (SQ)
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={isRefunded || isCancelled}
            onClick={() => setPendingKey("refund_payment")}
            className="text-amber-700 focus:text-amber-800"
            data-testid={`booking-action-refund-payment-${booking.id}`}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {isRefunded ? "Payment refunded" : "Refund payment"}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!hasHeldDeposit}
            onClick={() => setPendingKey("refund_deposit")}
            data-testid={`booking-action-refund-deposit-${booking.id}`}
          >
            <Banknote className="h-4 w-4 mr-2" />
            {booking.deposit_status === "released" ? "Deposit released" : booking.deposit_status === "withheld" ? "Deposit withheld" : "Refund deposit"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={isCancelled || isNoShow}
            onClick={() => setPendingKey("cancel")}
            className="text-rose-700 focus:text-rose-800"
            data-testid={`booking-action-cancel-${booking.id}`}
          >
            <XCircle className="h-4 w-4 mr-2" />
            {isCancelled ? "Reservation cancelled" : "Cancel reservation"}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!isConfirmed || isNoShow}
            onClick={() => setPendingKey("mark_no_show")}
            className="text-slate-700 focus:text-slate-800"
            data-testid={`booking-action-no-show-${booking.id}`}
          >
            <UserX className="h-4 w-4 mr-2" />
            {isNoShow ? "Marked No Show" : "Mark No Show"}
          </DropdownMenuItem>
          {/* Iter 69 — fallback collection for no-show fees when no
              security deposit was on file. Opens a Stripe Payment Link
              the vendor can forward to the renter via email/WhatsApp. */}
          {isNoShow && (booking.no_show_fee_eur || 0) > (booking.deposit_withheld_amount || 0) && (
            <DropdownMenuItem
              onClick={async () => {
                try {
                  const { data } = await api.post(`/bookings/${booking.id}/no-show-payment-link`);
                  await navigator.clipboard?.writeText?.(data.url).catch(() => {});
                  toast.success(`Payment link copied to clipboard. €${data.amount_eur.toFixed(2)}${data.reused ? " (existing link)" : ""}.`);
                  window.open(data.url, "_blank", "noopener,noreferrer");
                  onChanged?.();
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              data-testid={`booking-action-no-show-payment-link-${booking.id}`}
            >
              <Link2 className="h-4 w-4 mr-2" />
              {booking.no_show_payment_link_url ? "Open no-show payment link" : "Send no-show payment link"}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Suppress prefetch warnings for the hidden Link */}
      <Link to={`/agreements/${booking.id}`} className="hidden" aria-hidden="true" tabIndex={-1} />

      <Dialog open={!!pendingKey} onOpenChange={(o) => !o && !busy && setPendingKey(null)}>
        <DialogContent data-testid="booking-action-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{pendingKey ? ACTIONS[pendingKey].title : ""}</DialogTitle>
            <DialogDescription>{pendingKey ? ACTIONS[pendingKey].description : ""}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingKey(null)} disabled={busy} data-testid="booking-action-cancel-confirm">
              Cancel
            </Button>
            <Button
              onClick={runPending}
              disabled={busy}
              className={pendingKey === "refund_deposit" ? "" : "bg-rose-600 hover:bg-rose-700"}
              data-testid="booking-action-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {pendingKey ? ACTIONS[pendingKey].confirm : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendBookingDialog bookingId={booking.id} open={sendOpen} onClose={() => setSendOpen(false)} />
      {paymentsOpen && <PaymentsSheet booking={booking} onClose={() => { setPaymentsOpen(false); onChanged?.(); }} />}
      {editOpen && (
        <ManualBookingEditDialog booking={booking} open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => onChanged?.()} />
      )}
    </>
  );
}
